import { Inject, Injectable, Optional } from '@angular/core';
import { BehaviorSubject, ReplaySubject } from 'rxjs';

import { Capacitor } from '@capacitor/core';

// Prettier 1.18.2 cannot parse `import type`, and the pre-commit hook runs it,
// so the types ride along on the value import. TypeScript elides them.
import {
  ClogiUploader,
  ClogiUploaderPlugin,
  QueueItem,
  UploadProgressEvent,
  UploadStateChangeEvent,
} from 'capacitor-clogi-uploader';

import { ApiService } from '@services/api/api.service';
import { ClientIdService } from '@services/api/client-id.service';
import { PhotoMeta } from '@services/image-upload/image-upload.service';

import { photoUploadSource } from './photo-source';
import { UploadEntityType, uploadUrlsFor } from './upload-urls';

export interface EnqueuePhotoSpec {
  entityType: UploadEntityType;
  entityId: string;
  uploadUrl: string;
  originalFileName: string;
  photo: PhotoMeta;
}

export type QueueEventHandler = (event: any) => void;

/**
 * The only code that talks to the uploader plugin.
 *
 * Native owns the queue; this is a view onto it. Progress events stream only
 * while the app is foregrounded, so `sync()` re-reads native state on resume
 * -- which is what makes a stuck progress bar structurally impossible rather
 * than merely unlikely.
 */
@Injectable({ providedIn: 'root' })
export class UploadQueueService {
  private plugin: ClogiUploaderPlugin;
  private queue = new BehaviorSubject<QueueItem[]>([]);
  private synced = new ReplaySubject<QueueItem[]>(1);
  private started = false;
  private nativeEnabled = true;

  /**
   * Live view of the queue, byte counts included. Re-emits on every progress
   * tick, so subscribe to this only if you want the bytes.
   */
  readonly queue$ = this.queue.asObservable();

  /**
   * Emits only when native state has actually been re-read (`sync()`) or
   * discarded (`clearAuth()`) -- never for a progress tick.
   *
   * The store subscribes here. Progress reaches it through `uploadProgressed`
   * instead, so dispatching the whole array per chunk would be pure waste;
   * but a resume resync carries `bytesSent`, `attempts` and `lastError` that
   * have no other route into the store, so this stream is never filtered.
   * ReplaySubject(1) because the subscriber may be constructed after the
   * first sync has already run.
   */
  readonly queueSynced$ = this.synced.asObservable();

  constructor(
    private api: ApiService,
    private clientId: ClientIdService,
    @Optional() @Inject('CLOGI_UPLOADER') injected?: ClogiUploaderPlugin
  ) {
    this.plugin = injected || ClogiUploader;
  }

  /**
   * Called once from AppComponent, after the plugin exists.
   *
   * Guarded because a second call would register a second pair of listeners
   * with no way to remove the first: every progress and state event would
   * then fire the caller's handlers twice and sync() twice. AppComponent is
   * the only caller today, but a re-created Capacitor bridge would run its
   * initialisation again.
   */
  async start(onProgress: QueueEventHandler, onStateChange: QueueEventHandler) {
    if (this.started) {
      await this.sync();
      return;
    }
    this.started = true;

    await this.plugin.addListener(
      'uploadProgress',
      (event: UploadProgressEvent) => {
        this.applyProgress(event);
        onProgress(event);
      }
    );
    await this.plugin.addListener(
      'uploadStateChange',
      (event: UploadStateChangeEvent) => {
        onStateChange(event);
        void this.sync();
      }
    );
    await this.sync();
  }

  /**
   * Server-controlled kill switch, fed from base data (see
   * `selectNativeUploadsEnabled`). Native code cannot be hot-fixed, so a bad
   * build would otherwise cost an App Store review cycle with no photo
   * uploads at all; flipping the constance flag drops every client back onto
   * the legacy `S3UploadService` path on the next base-data load.
   */
  setEnabled(enabled: boolean) {
    this.nativeEnabled = enabled;
  }

  /**
   * All three conjuncts earn their place:
   *
   * - `nativeEnabled` is the server kill switch (see `setEnabled`).
   * - `isNativePlatform()` keeps the browser on the legacy path. A web
   *   implementation of the plugin does exist, so `isPluginAvailable` alone
   *   would answer true there; but on web there is no background upload
   *   service to gain from, and the legacy path is the tested one.
   * - `isPluginAvailable('ClogiUploader')` covers the case a server flag
   *   cannot: JS ships over the air (`@capawesome/capacitor-live-update`)
   *   onto an older native binary that has no uploader plugin at all. Taking
   *   the native branch there means `enqueue()` rejects and the photo is
   *   stranded with `imageExists: false` and no fallback.
   */
  get isNativeEnabled(): boolean {
    return (
      this.nativeEnabled &&
      Capacitor.isNativePlatform() &&
      Capacitor.isPluginAvailable('ClogiUploader')
    );
  }

  async enqueue(spec: EnqueuePhotoSpec): Promise<string> {
    const { refreshUrl, completeUrl } = uploadUrlsFor(
      this.api.url,
      spec.entityType,
      spec.entityId
    );

    // Native path where we have one -- never read the photo into the WebView
    // heap. A full-resolution capture is several megabytes, and six of them
    // as Blobs is a plausible out-of-memory kill on its own.
    const source = photoUploadSource(spec.photo);
    if (!source) {
      throw new Error(
        `enqueue: photo for ${spec.entityId} has neither a path nor a file`
      );
    }
    const { path, blob } = source;

    const { ids } = await this.plugin.enqueue({
      items: [
        {
          entityType: spec.entityType,
          entityId: spec.entityId,
          path,
          blob,
          mimeType: 'image/jpeg',
          originalFileName: spec.originalFileName,
          uploadUrl: spec.uploadUrl,
          refreshUrl,
          completeUrl,
        },
      ],
    });

    await this.sync();
    return ids[0];
  }

  async sync(): Promise<void> {
    const { items } = await this.plugin.getQueue();
    this.publish(items);
  }

  itemForEntity(entityId: string): QueueItem | undefined {
    return this.queue.getValue().find(item => item.entityId === entityId);
  }

  async retry(id: string): Promise<void> {
    await this.plugin.retry({ id });
    await this.sync();
  }

  async cancel(id: string): Promise<void> {
    await this.plugin.cancel({ id });
    await this.sync();
  }

  /**
   * The sync is not incidental: `clearAuth()` leaves the *native* queue
   * intact, so after a logout/login inside one session native may still be
   * uploading while our slice is empty. Nothing else re-reads it -- `start()`
   * is guarded by `started` -- so credentials returning is the trigger.
   */
  async setAuth(token: string): Promise<void> {
    await this.plugin.setAuth({ token, clientId: this.clientId.clientId });
    await this.sync();
  }

  /**
   * The local clear is unconditional. JS ships over the air without a new
   * native binary, so this can run on a build with no `ClogiUploader` at all;
   * if the rejection skipped the clear, the previous user's photos and
   * thumbnails would stay on screen after logout.
   */
  async clearAuth(): Promise<void> {
    try {
      await this.plugin.clearAuth();
    } finally {
      this.publish([]);
    }
  }

  private publish(items: QueueItem[]) {
    this.queue.next(items);
    this.synced.next(items);
  }

  /**
   * Progress arrives far too often to re-read the whole queue each time.
   * Deliberately pushes to `queue$` only -- see `queueSynced$`.
   */
  private applyProgress(event: UploadProgressEvent) {
    this.queue.next(
      this.queue.getValue().map(item =>
        item.id === event.id
          ? {
              ...item,
              bytesSent: event.bytesSent,
              bytesTotal: event.bytesTotal,
            }
          : item
      )
    );
  }
}
